import { useState } from "react";

import { ActionsMenu, Button, RenderWhen } from "@components";
import { ActionsModalNames } from "@components/ActionsModal";
import { NotificationsLevel } from "@js2/lib/enums";
import { show as showNotification } from "@js2/notifications/notifications";
import api from "@js2/services/api";
import { consts, settings } from "@js2/shim/jsbridge";
import tcbase from "@js2/shim/tcbase";
import { SUBSCRIPTION_ACTIONS } from "@modules/alertsconfiguration/const";
import { EnrichedFile } from "@modules/network/@types/common";
import AdvancedFetchModal from "@modules/samples/components/advanced_fetch/AdvancedFetchModal";
import useFetchAnalyze from "@modules/samples/hooks/useFetchAnalyze";
import { LOCATION } from "@modules/search/consts";
import useCloudReanalyze from "@modules/search/hooks/useCloudReanalyze";
import { copyToUploads } from "@modules/search/services";

const { FEATURE_ALERTFEED } = settings || {};
const { isSuperuser, userName } = tcbase || {};

const { ticloud_max_fetch_file_size: maxFetchSize } = consts ?? {};

type Props = {
  data: EnrichedFile;
  id?: string;
  onToggle: (values: unknown, name: string) => void;
};

const getCanUserDelete = (uploaders?: Array<string>) =>
  isSuperuser || (uploaders ?? []).includes(userName);

const getDeleteMessage = (canUserDelete: boolean, isContainer: boolean) => {
  if (!isContainer) return "Extracted files cannot be deleted.";
  if (!canUserDelete) return "You are not allowed to delete this file.";
  return null;
};

const getCanFetchMessage = (isDownloadable: boolean, size: number) => {
  if (!isDownloadable) return "Private files cannot be fetched.";
  if (size > maxFetchSize) return "File is too large to fetch.";
  return null;
};

const SingleActions = ({ data, id, onToggle }: Props) => {
  const [showAdvanced, setShowAdvanced] = useState(false);

  const { mutateAsync: fetchAndAnalyze } = useFetchAnalyze();
  const { cloudReanalyze } = useCloudReanalyze();

  const {
    sha1,
    filename,
    file_count: count,
    file_size: size,
    last_seen: lastSeen,
    processing_status: status,
    submission_users: users,
    submission_count: submissions,
    downloadable,
    location
  } = data ?? {};

  if (status === "PROCESSING") {
    return null;
  }

  const isContainer = Boolean(submissions);
  const isError = status?.toLowerCase() === "ERROR".toLowerCase();
  const canUserDelete = getCanUserDelete(users);
  const canDelete = (isContainer || isError) && canUserDelete;

  const fetchMessage = getCanFetchMessage(downloadable, size);

  const actions = [
    {
      label: "Delete Sample",
      show: location === LOCATION.LOCAL,
      disabled: !canDelete,
      disabledMessage: getDeleteMessage(canUserDelete, isContainer),
      onClick: () => onToggle({ hash: sha1 }, ActionsModalNames.Delete)
    },
    {
      label: "Copy To My Submissions",
      show: location === LOCATION.LOCAL,
      disabled: !isContainer,
      disabledMessage: "Extracted files cannot be copied to submissions.",
      onClick: () => copyToUploads(sha1, filename || sha1)
    },
    {
      label: "Download Container",
      show: location === LOCATION.LOCAL && Boolean(submissions),
      onClick: () =>
        api
          .getTopContainer({ hash: sha1 })
          .then(({ data: container }) => {
            const containerSha1 = container.container_sha1;
            onToggle(
              { sha1: containerSha1, file_names: [containerSha1] },
              ActionsModalNames.Download
            );
          })
          .catch(() => {
            showNotification({
              level: NotificationsLevel.ERROR,
              message: "Download Container Failed"
            });
          })
    },
    {
      label: "Download Sample",
      show: location === LOCATION.LOCAL,
      onClick: () => onToggle({ sha1 }, ActionsModalNames.Download)
    },
    {
      label: "Reanalyze",
      show: location === LOCATION.LOCAL,
      onClick: () =>
        onToggle(
          { samples: [{ hash_value: sha1 }] },
          ActionsModalNames.Reanalyze
        )
    },
    {
      label: "Set Classification",
      show: location === LOCATION.LOCAL,
      onClick: () =>
        onToggle({ hash: sha1 }, ActionsModalNames.UserClassification)
    },
    {
      label: "Download Extracted Files",
      show: location === LOCATION.LOCAL,
      disabled: count < 2,
      disabledMessage: "Sample does not have extracted files.",
      onClick: () => onToggle({ sha1 }, ActionsModalNames.DownloadUnpacked)
    },
    {
      label: "Subscribe",
      show: FEATURE_ALERTFEED,
      onClick: () => {
        const subscriptionData = {
          location,
          sha1,
          available: downloadable
        };
        onToggle(
          {
            subscriptionHashes: [subscriptionData],
            action: SUBSCRIPTION_ACTIONS.SUBSCRIBE
          },
          ActionsModalNames.Subscribehashes
        );
      }
    },
    {
      label: "Unsubscribe",
      show: FEATURE_ALERTFEED,
      onClick: () => {
        onToggle(
          {
            subscriptionHashes: [sha1],
            action: SUBSCRIPTION_ACTIONS.UNSUBSCRIBE
          },
          ActionsModalNames.Unsubscribehashes
        );
      }
    },
    {
      label: "Fetch & Analyze",
      show: location === LOCATION.CLOUD,
      disabled: Boolean(fetchMessage),
      disabledMessage: fetchMessage,
      onClick: () => fetchAndAnalyze({ sha1_hashes: [sha1] })
    },
    {
      label: "Fetch & Analyze (Advanced)",
      show: location === LOCATION.CLOUD,
      disabled: Boolean(fetchMessage),
      disabledMessage: fetchMessage,
      onClick: () => setShowAdvanced(true)
    },
    {
      label: "Reanalyze on Spectra Intelligence",
      show: location === LOCATION.CLOUD,
      disabled: !downloadable,
      disabledMessage: "Private files are not available for reanalyze",
      onClick: () => cloudReanalyze(sha1, lastSeen)
    }
  ];

  return (
    <>
      <ActionsMenu
        id={`${id}-${sha1}`}
        actions={actions.filter(({ show }) => show)}
        stopPropagationOnClick={false}
        buttonComponent={Button}
        useFixedPosition
      />
      <RenderWhen condition={Boolean(showAdvanced)}>
        <AdvancedFetchModal
          hashes={[sha1]}
          onClose={() => setShowAdvanced(false)}
        />
      </RenderWhen>
    </>
  );
};

export default SingleActions;
